import React from 'react';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import Dialog from 'ui/dialog';
import Select from 'ui/select';
import { selectStats } from 'logic/stats';
import { extendType, gemStats, itemGetTypes, itemMaxSockets, modsToStats, runewordStats, uniqueStats } from 'logic/item';
import { combineFilters } from 'utils';
import { skillTreeNames } from 'components/character/utils';
import { Quality } from 'common';

const _L = Locale("ui-equipment");

function baseFilter(type, func) {
  switch (type) {
  case 'normal': return { filter: id => func(Data.items[id]) };
  case 'crafted': return { base: base => func(Data.items[base]) };
  case 'runeword': return { base: base => func(Data.items[base]) };
  case 'unique': return { filter: id => func(Data.items[Data.uniqueItems[id]?.code]) };
  case 'set': return { filter: id => func(Data.items[Data.setItems[id]?.item]) };
  // no default
  }
}

const checkNumber = (stat, min, max) => (min == null || stat >= min) && (max == null || stat <= max);

const itemFilterRequirements = [
  {
    id: 'meta_levelreq',
    name: 'Required Level',
    type: 'number',
    noMinimum: true,
    min: 1,
    max: 99,
    defaultValue: state => ({max: state.profiles[state.activeProfile].level}),
    filter(type, min, max) {
      switch (type) {
      case 'normal': return { filter: id => (Data.items[id]?.levelreq || 0) <= max };
      case 'crafted': return { base: base => (Data.items[base]?.levelreq || 0) <= max };
      case 'runeword': return {
        filter: id => {
          const rw = Data.runes[id];
          return rw && [rw.rune1, rw.rune2, rw.rune3, rw.rune4, rw.rune5, rw.rune6].every(rune => !rune || (Data.items[rune].levelreq || 0) <= max);
        },
        base: base => Data.items[base]?.levelreq <= max,
      };
      case 'unique': return { filter: id => (Data.uniqueItems[id]?.lvlreq || 0) <= max && (Data.items[Data.uniqueItems[id].code].levelreq || 0) <= max };
      case 'set': return { filter: id => (Data.setItems[id]?.lvlreq || 0) <= max && (Data.items[Data.setItems[id].item].levelreq || 0) <= max };
      // no default
      }
    },
  },
  {
    id: 'meta_reqstr',
    name: 'Required Strength',
    type: 'number',
    min: 0,
    defaultValue: state => ({min: 0, max: selectStats(state).player.base.strength}),
    filter: (type, min, max) => baseFilter(type, base => checkNumber(base?.reqstr || 0, min, max)),
  },
  {
    id: 'meta_reqdex',
    name: 'Required Dexterity',
    type: 'number',
    min: 0,
    defaultValue: state => ({min: 0, max: selectStats(state).player.base.dexterity}),
    filter: (type, min, max) => baseFilter(type, base => checkNumber(base?.reqdex || 0, min, max)),
  },
  {
    id: 'meta_classreq',
    name: 'Usable by Class',
    paramType: 'class',
    defaultValue: state => ({param: Data.classes.indexOf(selectStats(state).player.class)}),
    filter: (type, min, max, param) => baseFilter(type, base => {
      const type = Data.itemTypes[base?.type];
      return type && (!type.class || type.class === Data.classes[param]);
    }),
  },
];

function checkMod(mod, types, rare) {
  if (mod.level > 99) return false;
  if (![mod.itype1, mod.itype2, mod.itype3, mod.itype4, mod.itype5, mod.itype6, mod.itype7].some(t => t && types.has(t))) return false;
  if ([mod.etype1, mod.etype2, mod.etype3, mod.etype4, mod.etype5].some(t => t && types.has(t))) return false;
  if (!mod.rare && rare) return false;
  return true;
}

let statCache = null;

function modMaxStats(id, mod) {
  const values = [];
  if (mod.mod1max != null) values[0] = mod.mod1max;
  if (mod.mod2max != null) values[1] = mod.mod2max;
  if (mod.mod3max != null) values[2] = mod.mod3max;
  if (mod.mod4max != null) values[3] = mod.mod4max;
  return modsToStats({}, {[id]: values});
}

function initCache() {
  if (!statCache) {
    const groups = {};
    for (const [id, mod] of Object.entries({...Data.magicPrefix, ...Data.magicSuffix})) {
      const stats = modMaxStats(id, mod);
      groups[mod.group] = groups[mod.group] || [];
      groups[mod.group].push({mod, stats});
    }
    statCache = Object.values(groups);
  }
}

function statFilter(type, func) {
  initCache();

  switch (type) {
  case 'normal':
    return {type(type) {
      const types = extendType(new Set(), type);
      const groups = statCache.map(g => g.filter(({mod}) => checkMod(mod, types)));
      return func(id => groups.reduce((sum, g) => sum + g.reduce((max, {stats}) => Math.max(max, stats[id] || 0), 0), 0));
    }};
  case 'crafted':
    return {type(id, type) {
      const recipe = Data.crafted[id];
      const stats = modMaxStats(id, recipe);

      const types = extendType(new Set(), type);
      const groups = statCache.map(g => g.filter(({mod}) => checkMod(mod, types, true)));
      return func(id => groups.reduce((sum, g) => sum + g.reduce((max, {stats}) => Math.max(max, stats[id] || 0), 0), stats[id] || 0));
    }};
  case 'unique':
  case 'set':
    return {filter(uniq) {
      const item = Data.uniqueItems[uniq] || Data.setItems[uniq];
      const values = [];
      for (let i = 1; i <= 12; ++i) {
        if (item[`max${i}`]) values[i - 1] = item[`max${i}`];
      }
      const stats = uniqueStats({}, item, values);
      return func(id => stats[id] || 0);
    }};
  case 'runeword':
    return {filter(rw, type) {
      const rune = Data.runes[rw];
      const values = [];
      for (let i = 1; i <= 7; ++i) {
        if (rune[`t1max${i}`]) values[i - 1] = rune[`t1max${i}`];
      }
      const stats = runewordStats({}, rune, values);
      const types = new Set([type]);
      for (let i = 1; i <= 6; ++i) {
        if (rune[`rune${i}`]) gemStats(stats, rune[`rune${i}`], types);
      }
      return func(id => stats[id] || 0);
    }};
  // no default
  }
}

function statFilterParam(type, id) {
  initCache();

  id += '#';

  switch (type) {
  case 'normal':
    return {type(type) {
      const types = extendType(new Set(), type);
      return statCache.some(g => g.some(({mod, stats}) => checkMod(mod, types) && Object.keys(stats).some(stat => stat.includes(id))));
    }};
  case 'crafted':
    return {type(id, type) {
      const recipe = Data.crafted[id];
      const stats = modMaxStats(id, recipe);
      if (Object.keys(stats).some(stat => stat.includes(id))) return true;

      const types = extendType(new Set(), type);
      return statCache.some(g => g.some(({mod, stats}) => checkMod(mod, types, true) && Object.keys(stats).some(stat => stat.includes(id))));
    }};
  case 'unique':
  case 'set':
    return {filter(uniq) {
      const item = Data.uniqueItems[uniq] || Data.setItems[uniq];
      const values = [];
      for (let i = 1; i <= 12; ++i) {
        if (item[`max${i}`]) values[i - 1] = item[`max${i}`];
      }
      const stats = uniqueStats({}, item, values);
      return Object.keys(stats).some(stat => stat.includes(id));
    }};
  case 'runeword':
    return {filter(rw) {
      const rune = Data.runes[rw];
      const values = [];
      for (let i = 1; i <= 7; ++i) {
        if (rune[`t1max${i}`]) values[i - 1] = rune[`t1max${i}`];
      }
      const stats = runewordStats({}, rune, values);
      return Object.keys(stats).some(stat => stat.includes(id));
    }};
  // no default
  }
}

const statFilterSingle = (name, id) => ({
  id,
  name,
  type: 'number',
  filter: (type, min, max) => statFilter(type, stats => checkNumber(stats(id), min, max)),
});

const statFilterBoolean = (name, id) => ({
  id,
  name,
  type: 'boolean',
  filter: (type, value) => statFilter(type, stats => !stats(id) === !value),
});

const statFilterPerLevel = (name, id, idlvl) => ({
  id,
  name,
  type: 'number',
  filter(type, min, max, param, state) {
    const mult = state.profiles[state.activeProfile].level / (1 << Data.itemStatCost[idlvl].opparam);
    return statFilter(type, stats => checkNumber(stats(id) + (stats(idlvl) * mult | 0), min, max));
  },
});

const itemFilterAttributes = [
  statFilterPerLevel('Strength', 'strength', 'item_strength_perlevel'),
  statFilterPerLevel('Dexterity', 'dexterity', 'item_dexterity_perlevel'),
  statFilterPerLevel('Energy', 'energy', 'item_energy_perlevel'),
  statFilterPerLevel('Vitality', 'vitality', 'item_vitality_perlevel'),
  statFilterPerLevel('Maximum Life', 'maxhp', 'item_hp_perlevel'),
  statFilterSingle('Increase Maximum Life %', 'item_maxhp_percent'),
  statFilterPerLevel('Maximum Mana', 'maxmana', 'item_mana_perlevel'),
  statFilterSingle('Increase Maximum Mana %', 'item_maxmana_percent'),
  statFilterPerLevel('Maximum Stamina', 'maxstamina', 'item_stamina_perlevel'),
];

const itemFilterDefenses = [
  statFilterPerLevel('Added Defense', 'armorclass', 'item_armor_perlevel'),
  statFilterPerLevel('Enhanced Defense %', 'item_armor_percent', 'item_armorpercent_perlevel'),
  statFilterSingle('Damage Reduction', 'normal_damage_reduction'),
  statFilterSingle('Damage Reduction %', 'damageresist'),
  statFilterSingle('Magic Damage Reduction', 'magic_damage_reduction'),
  statFilterSingle('Block Chance', 'toblock'),
  statFilterSingle('Faster Hit Recovery', 'item_fastergethitrate'),
  statFilterSingle('Faster Block Recovery', 'item_fasterblockrate'),
];

const itemFilterResistances = [
  statFilterPerLevel('Fire Resist', 'fireresist', 'item_resist_fire_perlevel'),
  statFilterSingle('Maximum Fire Resist', 'maxfireresist'),
  statFilterPerLevel('Cold Resist', 'coldresist', 'item_resist_cold_perlevel'),
  statFilterSingle('Maximum Cold Resist', 'maxcoldresist'),
  statFilterPerLevel('Lightning Resist', 'lightresist', 'item_resist_ltng_perlevel'),
  statFilterSingle('Maximum Lightning Resist', 'maxlightresist'),
  statFilterPerLevel('Poison Resist', 'poisonresist', 'item_resist_pois_perlevel'),
  statFilterSingle('Maximum Poison Resist', 'maxpoisonresist'),
  statFilterSingle('Magic Resist', 'magicresist'),
  statFilterSingle('Maximum Magic Resist', 'maxmagicresist'),
  {
    id: 'meta_total_resist',
    name: 'Total Resistance',
    type: 'number',
    filter: (type, min, max) => statFilter(type, stats =>
        checkNumber(stats('fireresist') + stats('coldresist') + stats('lightresist') + stats('poisonresist') + stats('magicresist'), min, max)),
  },
  statFilterPerLevel('Fire Absorb', 'item_absorbfire', 'item_absorb_fire_perlevel'),
  statFilterSingle('Fire Absorb %', 'item_absorbfire_percent'),
  statFilterPerLevel('Cold Absorb', 'item_absorbcold', 'item_absorb_cold_perlevel'),
  statFilterSingle('Cold Absorb %', 'item_absorbcold_percent'),
  statFilterPerLevel('Lightning Absorb', 'item_absorblight', 'item_absorb_ltng_perlevel'),
  statFilterSingle('Lightning Absorb %', 'item_absorblight_percent'),
  statFilterPerLevel('Magic Absorb', 'item_absorbmagic', 'item_absorb_pois_perlevel'), // yes, poison/level is actually magic absorb
  statFilterSingle('Magic Absorb %', 'item_absorbmagic_percent'),
  statFilterSingle('Poison Length Reduction', 'item_poisonlengthresist'),
  statFilterBoolean('Half Freeze Duration', 'item_halffreezeduration'),
  statFilterBoolean('Cannot Be Frozen', 'item_cannotbefrozen'),
];

const itemFilterDamage = [
  {
    id: 'item_maxdamage_percent',
    name: 'Enhanced Damage %',
    type: 'number',
    filter(type, min, max, param, state) {
      const mult = state.profiles[state.activeProfile].level / 16;
      return statFilter(type, stats => checkNumber(stats('item_maxdamage_percent') + (stats('item_maxdamage_percent_perlevel') * mult | 0), min, max));
    },
  },
  statFilterSingle('Added Minimum Damage', 'mindamage'),
  statFilterPerLevel('Added Maximum Damage', 'maxdamage', 'item_maxdamage_perlevel'),
  statFilterSingle('Minimum Fire Damage', 'firemindam'),
  statFilterPerLevel('Maximum Fire Damage', 'firemaxdam', 'item_fire_damagemax_perlevel'),
  statFilterSingle('Minimum Cold Damage', 'coldmindam'),
  statFilterPerLevel('Maximum Cold Damage', 'coldmaxdam', 'item_cold_damagemax_perlevel'),
  statFilterSingle('Minimum Lightning Damage', 'lightmindam'),
  statFilterPerLevel('Maximum Lightning Damage', 'lightmaxdam', 'item_ltng_damagemax_perlevel'),
  {
    id: 'poisonmaxdam',
    name: 'Poison Damage',
    type: 'number',
    filter: (type, min, max) => statFilter(type, stats => checkNumber(stats('poisonmaxdam') * stats('poisonlength') / 256, min, max)),
  },
  statFilterSingle('Minimum Magic Damage', 'magicmindam'),
  statFilterSingle('Maximum Magic Damage', 'magicmaxdam'),
];

const itemFilterOffensive = [
  statFilterPerLevel('Attack Rating', 'tohit', 'item_tohit_perlevel'),
  statFilterPerLevel('Attack Rating %', 'item_tohit_percent', 'item_tohitpercent_perlevel'),
  statFilterSingle('Increased Attack Speed', 'item_fasterattackrate'),
  statFilterBoolean('Ignore Target\'s Defense', 'item_ignoretargetac'),
  statFilterSingle('Ignore Defense %', 'item_fractionaltargetac'),
  statFilterPerLevel('Open Wounds %', 'item_openwounds', 'item_openwounds_perlevel'),
  statFilterPerLevel('Crushing Blow %', 'item_crushingblow', 'item_crushingblow_perlevel'),
  statFilterPerLevel('Deadly Strike %', 'item_deadlystrike', 'item_deadlystrike_perlevel'),
  statFilterSingle('Pierce %', 'item_pierce'),
  statFilterSingle('Faster Cast Rate', 'item_fastercastrate'),
  statFilterSingle('Fire Damage %', 'passive_fire_mastery'),
  statFilterSingle('Cold Damage %', 'passive_cold_mastery'),
  statFilterSingle('Lightning Damage %', 'passive_ltng_mastery'),
  statFilterSingle('Poison Damage %', 'passive_pois_mastery'),
  statFilterSingle('Fire Pierce %', 'passive_fire_pierce'),
  statFilterSingle('Cold Pierce %', 'passive_cold_pierce'),
  statFilterSingle('Lightning Pierce %', 'passive_ltng_pierce'),
  statFilterSingle('Poison Pierce %', 'passive_pois_pierce'),
];

const statFilterSkills = (name, id, paramType, defaultValue) => ({
  id,
  name,
  type: 'number',
  paramType,
  defaultValue,
  filter: (type, min, max, param) => (param !== -1 ? statFilter(type, stats => checkNumber(stats(`${id}#${param}`), min, max)) : statFilterParam(type, id)),
});

const itemFilterSkills = [
  statFilterSingle('All Skill Levels', 'item_allskills'),
  statFilterSingle('Fire Skill Levels', 'item_elemskill#1'),
  statFilterSkills('Class Skill Levels', 'item_addclassskills', 'class', state => ({param: Data.classes.indexOf(selectStats(state).player.class)})),
  statFilterSkills('Skill Tab Levels', 'item_addskill_tab', 'skilltab', state => ({param: Data.classes.indexOf(selectStats(state).player.class) * 3})),
  statFilterSkills('Single Skill Level', 'item_singleskill', 'skill', state => {
    const cls = selectStats(state).player.class;
    return {param: Object.keys(Data.skills).find(id => Data.skills[id].charclass === cls)};
  }),
  statFilterSkills('Non-Class Skill', 'item_nonclassskill', 'skill', () => ({param: -1})),
  statFilterSkills('Aura', 'item_aura', 'aura', () => ({param: -1})),
  {
    id: 'item_charged_skill',
    name: 'Charged Skill',
    paramType: 'skill',
    defaultValue: () => ({param: -1}),
    filter: (type, min, max, param) => statFilterParam(type, param !== -1 ? `item_charged_skill#${param}` : 'item_charged_skill'),
  },
];

const itemFilterMisc = [
  {
    id: 'item_numsockets',
    name: 'Sockets',
    type: 'number',
    noMaximum: true,
    min: 0,
    max: 6,
    filter(type, min, max) {
      initCache();
    
      switch (type) {
      case 'normal': return {filter: id => itemMaxSockets(Data.items[id], Quality.NORMAL) >= min};
      case 'crafted': return {base: id => itemMaxSockets(Data.items[id], Quality.CRAFTED) >= min};
      case 'unique':
      case 'set':
        return {filter(uniq) {
          const item = Data.uniqueItems[uniq] || Data.setItems[uniq];
          if (itemMaxSockets(Data.items[item.code || item.item], Quality.UNIQUE) >= min) return true;
          const values = [];
          for (let i = 1; i <= 12; ++i) {
            if (item[`max${i}`]) values[i - 1] = item[`max${i}`];
          }
          const stats = uniqueStats({}, item, values);
          return stats.item_numsockets >= min;
        }};
      case 'runeword':
        return {filter(rw) {
          const rune = Data.runes[rw];
          return [rune.rune1, rune.rune2, rune.rune3, rune.rune4, rune.rune5, rune.rune6].filter(Boolean).length >= min;
        }};
      // no default
      }
    },
  },
  statFilterSingle('Replenish Life', 'hpregen'),
  statFilterSingle('Regenerate Mana', 'manarecoverybonus'),
  statFilterPerLevel('Heal Stamina Plus', 'staminarecoverybonus', 'item_regenstamina_perlevel'),
  statFilterSingle('Slower Stamina Drain', 'item_staminadrainpct'),
  statFilterSingle('Life stolen per hit', 'lifedrainmindam'),
  statFilterSingle('Mana stolen per hit', 'manadrainmindam'),
  statFilterSingle('Life after each Kill', 'item_healafterkill'),
  statFilterSingle('Mana after each Kill', 'item_manaafterkill'),
  statFilterPerLevel('Gold Find', 'item_goldbonus', 'item_find_gold_perlevel'),
  statFilterPerLevel('Magic Find', 'item_magicbonus', 'item_find_magic_perlevel'),
  statFilterPerLevel('Attacker Takes Damage', 'item_attackertakesdamage', 'item_thorns_perlevel'),
  statFilterSingle('Light Radius', 'item_lightradius'),
  statFilterSingle('Faster Run/Walk', 'item_fastermovevelocity'),
  statFilterBoolean('Knockback', 'item_knockback'),
  statFilterBoolean('Hit Causes Monster to Flee', 'item_howl'),
  statFilterBoolean('Hit Blinds Target', 'item_stupidity'),
  statFilterBoolean('Prevent Monster Heal', 'item_preventheal'),
  statFilterBoolean('Freezes target', 'item_freeze'),
  statFilterBoolean('Slows Target', 'item_slow'),
  statFilterSingle('Slain Monsters Rest in Peace', 'item_restinpeace'),
  statFilterSingle('Damage Taken Goes To Mana', 'item_damagetomana'),
  statFilterSingle('Repairs durability', 'item_replenish_durability'),
  statFilterSingle('Replenishes quantity', 'item_replenish_quantity'),
  {
    id: 'item_indesctructible',
    name: 'Indestructible',
    type: 'boolean',
    filter(type, value) {
      initCache();
    
      switch (type) {
      case 'normal':
        return {filter(id) {
          if (Data.items[id].nodurability) return !!value;
          const types = itemGetTypes(id);
          return statCache.some(g => g.some(({mod, stats}) => checkMod(mod, types) && stats.item_indesctructible)) === !!value;
        }};
      case 'crafted':
        return {base(id) {
          if (Data.items[id].nodurability) return !!value;
          const types = itemGetTypes(id);
          return statCache.some(g => g.some(({mod, stats}) => checkMod(mod, types, true) && stats.item_indesctructible)) === !!value;
        }};
      case 'unique':
      case 'set':
        return {filter(uniq) {
          const item = Data.uniqueItems[uniq] || Data.setItems[uniq];
          if (Data.items[item.code || item.item]?.nodurability) return !!value;
          const values = [];
          for (let i = 1; i <= 12; ++i) {
            if (item[`max${i}`]) values[i - 1] = item[`max${i}`];
          }
          const stats = uniqueStats({}, item, values);
          return !stats.item_indesctructible === !value;
        }};
      case 'runeword':
        return {filter(rw, type) {
          const rune = Data.runes[rw];
          const values = [];
          for (let i = 1; i <= 7; ++i) {
            if (rune[`t1max${i}`]) values[i - 1] = rune[`t1max${i}`];
          }
          const stats = runewordStats({}, rune, values);
          const types = new Set([type]);
          for (let i = 1; i <= 6; ++i) {
            if (rune[`rune${i}`]) gemStats(stats, rune[`rune${i}`], types);
          }
          return !stats.item_indesctructible === !value;
        }};
      // no default
      }
    },
  },
];

const itemFilters = {};
for (const filter of itemFilterRequirements) itemFilters[filter.id] = filter;
for (const filter of itemFilterAttributes) itemFilters[filter.id] = filter;
for (const filter of itemFilterDefenses) itemFilters[filter.id] = filter;
for (const filter of itemFilterResistances) itemFilters[filter.id] = filter;
for (const filter of itemFilterDamage) itemFilters[filter.id] = filter;
for (const filter of itemFilterOffensive) itemFilters[filter.id] = filter;
for (const filter of itemFilterSkills) itemFilters[filter.id] = filter;
for (const filter of itemFilterMisc) itemFilters[filter.id] = filter;

export function createItemFilter(filter = [], base = [], type = []) {
  const func = list => ((...args) => list.every(f => !f || f(...args)));
  return {
    funcs: {filter, base, type},
    filter: func(filter),
    base: func(base),
    type: func(type),
  };
}

export function itemFilterFunction(filters, type, state) {
  const filterFunc = filters.map(({id, min, max, param}) => itemFilters[id]?.filter(type, min, max, param, state)).filter(Boolean);
  if (!filterFunc.length) return;
  return createItemFilter(
    filterFunc.map(f => f.filter).filter(Boolean),
    filterFunc.map(f => f.base).filter(Boolean),
    filterFunc.map(f => f.type).filter(Boolean)
  );
}

const classOptions = () => [{id: -1, name: _L`Any Class`}, ...Data.classes.map((cls, id) => ({id, name: Locale.strings[Data.classNames[cls]]}))];

const skilltabOptions = () => [
  {id: -1, name: _L`Any Skill Tab`},
  ...Data.classes.map((cls, cid) => ({
    title: Locale.strings[Data.classNames[cls]],
    options: skillTreeNames[cls].map((name, idx) => ({id: cid * 3 + idx, name: name.map(n => Locale.strings[n]).join(' ')})),
  })),
];
function skilltabName(idx) {
  const cls = Data.classes[Math.floor(idx / 3)];
  const tabs = skillTreeNames[cls]?.[idx % 3];
  if (!tabs) return _L`Any Skill Tab`;
  return tabs.map(n => Locale.strings[n]).join(' ');
}

const skillName = id => Locale.strings[Data.skillDesc[Data.skills[id]?.skilldesc]?.strname] || _L`Any Skill`;
const skillOptions = () => [
  {id: -1, name: _L`Any Skill`},
  ...Data.classes.map(cls => ({
    title: Locale.strings[Data.classNames[cls]],
    options: Object.keys(Data.skills).filter(id => Data.skills[id].charclass === cls).map(id => ({id, name: skillName(id)})),
  })),
];
const auraOptions = () => [
  {id: -1, name: _L`Any Aura`},
  ...Object.keys(Data.skills).filter(id => Data.skills[id].charclass === 'pal' && Data.skills[id].aura).map(id => ({id, name: skillName(id)})),
];

function ItemFilterLine({ value, onChange }) {
  const store = Store.useStore();

  const getOptions = React.useCallback(() => [
    {title: _L`Requirements`, options: itemFilterRequirements},
    {title: _L`Attributes`, options: itemFilterAttributes},
    {title: _L`Defenses`, options: itemFilterDefenses},
    {title: _L`Resistances`, options: itemFilterResistances},
    {title: _L`Damage`, options: itemFilterDamage},
    {title: _L`Offense`, options: itemFilterOffensive},
    {title: _L`Skills`, options: itemFilterSkills},
    {title: _L`Misc`, options: itemFilterMisc},
  ], []);

  const onChangeFilter = React.useCallback(value => {
    if (value) {
      const filter = itemFilters[value.id];
      const {min=1, max, param} = filter.defaultValue?.(store.getState()) || {};
      onChange({id: value.id, min, max, param});
    } else {
      onChange(null);
    }
  }, [store, onChange]);
  const onChangeMin = React.useCallback(e => onChange(value => ({...value, min: e.target.value ? parseInt(e.target.value) : null})), [onChange]);
  const onChangeMax = React.useCallback(e => onChange(value => ({...value, max: e.target.value ? parseInt(e.target.value) : null})), [onChange]);
  const onChangeBoolean = React.useCallback(opt => onChange(value => ({...value, min: parseInt(opt.id)})), [onChange]);
  const onChangeParam = React.useCallback(opt => onChange(value => ({...value, param: opt.id})), [onChange]);

  const info = value && itemFilters[value.id];
  const {noMinimum, noMaximum, type, paramType} = info || {};

  const valueRef = React.useRef();
  React.useEffect(() => valueRef.current?.focus(), [info]);

  const boolOptions = React.useMemo(() => [{id: '0', name: _L`No`}, {id: '1', name: _L`Yes`}], []);

  let values;
  if (type === 'number') {
    values = <>
      <input type="number" className="d2p-filter-min" min={info.min} max={info.max} value={noMinimum ? '' : value.min ?? ''}
        onChange={onChangeMin} ref={noMinimum ? null : valueRef} disabled={noMinimum} placeholder={_L`Min`}/>
      <span className="d2p-filter-dash"> {_L`to`} </span>
      <input type="number" className="d2p-filter-max" min={info.min} max={info.max} value={noMaximum ? '' : value.max ?? ''}
        onChange={onChangeMax} ref={noMinimum ? valueRef : null} disabled={noMaximum} placeholder={_L`Max`}/>
    </>;
  } else if (type === 'boolean') {
    values = <>
      <Select options={boolOptions}
              value={boolOptions[value.min ? 1 : 0]}
              onChange={onChangeBoolean}
              className="d2p-filter-min"
              />
      <span className="d2p-filter-dash"/>
      <span className="d2p-filter-max"/>
    </>;
  }

  return (<>
    <div className="d2p-ItemFilterLine">
      <Select options={getOptions}
              value={value && itemFilters[value.id]}
              onChange={onChangeFilter}
              searchLimit={10}
              className="d2p-filter-stat"
              allowEmpty/>
      {!paramType && values}
      {paramType === 'class' && (
        <Select options={classOptions}
                value={{id: value.param, name: Locale.strings[Data.classNames[Data.classes[value.param]]] || _L`Any Class`}}
                onChange={onChangeParam}
                className="d2p-filter-param"
                />
      )}
      {paramType === 'skilltab' && (
        <Select options={skilltabOptions}
                value={{id: value.param, name: skilltabName(value.param)}}
                onChange={onChangeParam}
                className="d2p-filter-param"
                searchLimit={10}
                />
      )}
      {(paramType === 'skill' || paramType === 'aura') && (
        <Select options={paramType === 'skill' ? skillOptions : auraOptions}
                value={{id: value.param, name: skillName(value.param)}}
                onChange={onChangeParam}
                className="d2p-filter-param"
                searchLimit={10}
                />
      )}
    </div>
    {!!(paramType && values && value.param !== -1) && (
      <div className="d2p-ItemFilterLine">
        <div className="d2p-filter-stat"/>
        {values}
      </div>
    )}
  </>);
}

export default function ItemFilterDialog({ onClose, onAccept, initialValue }) {
  const [filters, setFilters] = React.useState(initialValue);

  const onKeyDown = React.useCallback(e => {
    if (e.key === "Enter") {
      onAccept(filters);
    }
  }, [onAccept, filters]);

  const onChange = (index, value) => setFilters(filters => {
    filters = filters.slice();
    if (typeof value === 'function') value = value(filters[index]);
    if (value) {
      filters[index] = value;
    } else {
      filters.splice(index, 1);
    }
    return filters;
  });

  return (
    <Dialog title={_L`Filter items`} modal onClose={onClose} className="d2p-ItemFilterDialog" onKeyDown={onKeyDown}>
      <Dialog.Content>
        {[...filters, null].map((value, index) => <ItemFilterLine key={index} value={value} onChange={value => onChange(index, value)}/>)}
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onAccept(filters)}>{_L`Filter`}</button>
        <button onClick={onClose}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}
